import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useHandleMessage } from "@/features/messages/hooks/use-messages";
import type { ClientMessage } from "@/features/messages/types";
import { useTranslation } from "@/features/utils/i18n";
import { CheckIcon, ProhibitIcon } from "@phosphor-icons/react";
import { useMemo } from "react";

/**
 * What the team does to several requests at once: close them. The agent's
 * call back is left out on purpose — it needs an answer and a time per request.
 * Only the requests still to do are touched.
 */
export const useMessagesBulkActions = (): BulkAction<ClientMessage>[] => {
  const { t } = useTranslation("messages");
  const handleMessage = useHandleMessage();

  return useMemo(() => {
    const pending = (rows: ClientMessage[]) =>
      rows.filter((row) => row.status === "pending");

    const close = (
      key: "handledByHuman" | "dropped",
      resolution: "handled_by_human" | "dropped",
      icon: React.ReactNode,
    ): BulkAction<ClientMessage> => ({
      key,
      label: t(`bulk.actions.${key}`),
      icon,
      variant: resolution === "dropped" ? "destructive" : undefined,
      isVisible: (rows) => pending(rows).length > 0,
      confirm:
        resolution === "dropped"
          ? (rows) => t("bulk.droppedConfirm", { count: pending(rows).length })
          : undefined,
      onAction: async (rows) => {
        await runBulk(
          pending(rows),
          (message) =>
            handleMessage.mutateAsync({ id: message.id, resolution }),
          {
            success: (count) => t(`bulk.${key}Done`, { count }),
            error: (count) => t("bulk.failed", { count }),
          },
        );
      },
    });

    return [
      close(
        "handledByHuman",
        "handled_by_human",
        <CheckIcon className="h-4 w-4" />,
      ),
      close("dropped", "dropped", <ProhibitIcon className="h-4 w-4" />),
    ];
  }, [t, handleMessage]);
};
